import React, { useEffect } from 'react';
import ModalContainer from 'components/shared/modal/modal_container';
import RenderInBody from 'components/shared/render_in_body';
import { useQueryClient } from '@tanstack/react-query';
import { Button, ButtonGroup, Icon } from 'ui';
import { Text, TEXT_VARIANTS } from 'components/ui/Typography';
import { REACT_QUERY_STATUS } from 'constants/config';
import { withSkillsClientWrapper } from 'components/shared/Skills/SkillsClientProvider';
import { withReactQueryWrapper } from 'components/shared/ReactQuery/ReactQueryProvider';
import {
  useDeleteReadinessSkillCategory,
  useDeleteReadinessSkill,
} from '../queries/readinessSkills';

const DeleteModal = ({
  closeDeleteModal,
  closeModal,
  formData,
  setBannerProperties,
  setEditModalErrorText,
  deleteCategory,
  deleteSkill,
}) => {
  const { mutate, status } = useDeleteReadinessSkillCategory({
    id: '',
  });
  const { mutate: deleteSkillMutate, status: deleteSkillStatus } =
    useDeleteReadinessSkill({
      id: '',
    });
  const queryClient = useQueryClient();

  async function handleDeleteCategory() {
    try {
      await mutate(
        {
          id: formData.id,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('get-readiness-skills-categories');
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
  }

  async function handleDeleteSkill() {
    try {
      await deleteSkillMutate(
        {
          id: formData.id,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('get-readiness-skills');
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (status === REACT_QUERY_STATUS.SUCCESS) {
      closeDeleteModal();
      closeModal();
      setBannerProperties({
        message: (
          <p
            dangerouslySetInnerHTML={{
              __html: t('skills.banner_category_deleted', {
                category_name: formData.name,
              }),
            }}
          />
        ),
        state: 'success',
      });
    }
    if (status === REACT_QUERY_STATUS.ERROR) {
      setEditModalErrorText(
        t('skills.banner_delete_error', {
          category_name: formData.name,
        }),
      );
      closeDeleteModal();
    }
    if (deleteSkillStatus === REACT_QUERY_STATUS.SUCCESS) {
      closeDeleteModal();
      setBannerProperties({
        message: (
          <p
            dangerouslySetInnerHTML={{
              __html: t('skills.banner_skill_deleted', {
                skill_name: formData.name,
              }),
            }}
          />
        ),
        state: 'success',
      });
    }
    if (deleteSkillStatus === REACT_QUERY_STATUS.ERROR) {
      setBannerProperties({
        message: (
          <p
            dangerouslySetInnerHTML={{
              __html: t('skills.banner_skill_delete_error', {
                skill_name: formData.name,
              }),
            }}
          />
        ),
        state: 'error',
      });
      closeDeleteModal();
    }
  }, [
    status,
    formData.name,
    deleteSkillStatus,
    closeDeleteModal,
    closeModal,
    setBannerProperties,
    setEditModalErrorText,
  ]);

  return (
    <RenderInBody>
      <ModalContainer
        className="ModalTakeover-background Modal-background d-f ai-c"
        onEscape={closeDeleteModal}
      >
        {deleteCategory && (
          <div className="ModalTakeover-container w-55p">
            <div className="TemplateCreationModal-body">
              <div className="TemplateCreationModal-title">
                {t('skills.delete_this_category')}
              </div>
              <div className="mt-16">
                <Text variant={TEXT_VARIANTS.BODY}>
                  {t('skills.delete_question')}
                </Text>
              </div>
              <div className="mt-24">
                <ButtonGroup alignEnd>
                  <Button type="cancel" onClick={closeDeleteModal}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    className="Button--danger"
                    onClick={handleDeleteCategory}
                  >
                    {t('skills.delete_category')}
                  </Button>
                </ButtonGroup>
              </div>
              <button
                type="button"
                aria-label={t('common.close')}
                className="u-resetBtn ModalTakeover-closeButton"
                onClick={closeDeleteModal}
              >
                <Icon name="error" />
              </button>
            </div>
          </div>
        )}
        {deleteSkill && (
          <div className="ModalTakeover-container w-55p">
            <div className="TemplateCreationModal-body">
              <div className="TemplateCreationModal-title">
                {t('skills.delete_this_skill')}
              </div>
              <div className="mt-16">
                <Text variant={TEXT_VARIANTS.BODY}>
                  {t('skills.delete_skill_question')}
                </Text>
              </div>
              <div className="mt-24">
                <ButtonGroup alignEnd>
                  <Button type="cancel" onClick={closeDeleteModal}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    className="Button--danger"
                    onClick={handleDeleteSkill}
                  >
                    {t('skills.delete_skill')}
                  </Button>
                </ButtonGroup>
              </div>
              <button
                type="button"
                aria-label={t('common.close')}
                className="u-resetBtn ModalTakeover-closeButton"
                onClick={closeDeleteModal}
              >
                <Icon name="error" />
              </button>
            </div>
          </div>
        )}
      </ModalContainer>
    </RenderInBody>
  );
};

export default withSkillsClientWrapper(withReactQueryWrapper(DeleteModal));
